import { LabCombinedSettings, LabEnhanceSettings, LabStageSettings } from './labSdk.types'
import { isLabEnhanceSettingsValid, isLabStageSettingsValid } from './labSdk.mapping'

/**
 * "Display all features at once": the Lab tools as sections of one list, combined into one Generate.
 * Stage, Enhance and Erase each have a switch (runs in the next Generate); Blur and Adjust are applied instantly and
 * have none. The AI label sits in the Stage section, under its options, as in the One tool Stage panel.
 */
export type LabSection = 'stage' | 'enhance' | 'erase'
export type LabSectionState = Record<LabSection, boolean>
export type LabListSection = LabSection | 'adjust'

/** Per-browser memory of the view and its first-run hint (a convenience: unreadable storage = not set) */
export const ALL_FEATURES_STORAGE_KEYS = {
  /** The chosen view: "Combine tools" when 'true' */
  view: 'photoStudio.allFeaturesView',
  /** The first-run hint was answered (Try it / Not now / any choice) */
  hintSeen: 'photoStudio.allFeaturesHintSeen',
  /** "Combine tools" was picked at least once — its "New" badge goes */
  used: 'photoStudio.allFeaturesViewUsed',
} as const

export const readStoredFlag = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

export const writeStoredFlag = (key: string, value: boolean): void => {
  try {
    window.localStorage.setItem(key, String(value))
  } catch {
    // Storage unavailable (private mode, blocked site data): the choice lasts for this visit
  }
}

/** Stage and Erase are both generative edits, and the Lab runs one per order */
const EXCLUSIVE_SECTION: Partial<Record<LabSection, LabSection>> = { stage: 'erase', erase: 'stage' }

export type LabSectionsOpen = Record<LabListSection, boolean>

/**
 * Seeded from the tool on screen when the view is switched on, so the next Generate runs what it would have run:
 * only that section is on. Expanded sections: the first time in a Photo Studio session only Stage (2026-10-08); later
 * in the session the list reopens as it was left (`previousOpen`, kept in the Photo Studio context). Adjust is always
 * expanded when it is the tool on screen, so its "Done adjusting" is visible. Blur has no stored state: its section is
 * open exactly while Blur is on the canvas (LabAllFeaturesPanel), and as a tool on screen it turns nothing on.
 */
export const initialLabSections = (tool: LabListSection | 'blur', previousOpen?: LabSectionsOpen | null): { on: LabSectionState, open: LabSectionsOpen } => {
  const open = previousOpen ?? { stage: true, enhance: false, erase: false, adjust: false }
  return {
    on: { stage: tool === 'stage', enhance: tool === 'enhance', erase: tool === 'erase' },
    open: { ...open, adjust: open.adjust || tool === 'adjust' },
  }
}

/** Turning Stage or Erase on turns the other one off — `turnedOff` names it, so the panel can say so and offer Undo */
export const toggleLabSection = (on: LabSectionState, section: LabSection, value: boolean): { on: LabSectionState, turnedOff?: LabSection } => {
  const next = { ...on, [section]: value }
  const other = EXCLUSIVE_SECTION[section]
  if (value && other && on[other]) {
    next[other] = false
    return { on: next, turnedOff: other }
  }
  return { on: next }
}

/** The tool the canvas shows for the sections that are on: the eraser's mask drawer while Erase is on */
export const resolveLabCanvasSection = (on: LabSectionState): LabSection => (on.erase ? 'erase' : on.stage ? 'stage' : 'enhance')

export const toLabCombinedSettings = (on: LabSectionState, stage: LabStageSettings, enhance: LabEnhanceSettings): LabCombinedSettings => ({
  enhance: on.enhance ? enhance : undefined,
  generative: on.erase ? { kind: 'erase' } : on.stage ? { kind: 'stage', settings: stage } : undefined,
})

interface LabAllFeaturesBlockerParams {
  on: LabSectionState
  stage: LabStageSettings
  enhance: LabEnhanceSettings
  /** A new mask (or object) is marked since the last erase */
  canErase: boolean
  /** An erase already ran — the mask tooltip then asks for a new one */
  hasErased: boolean
  isBusy: boolean
}

/** Why Generate is disabled, as a `photo_studio_editing` translation key — null when it can run */
// An open Adjust tool no longer blocks (2026-10-08): Generate commits the Adjust edits first (LabAllFeaturesPanel).
// The `lab.all_features.close_adjust` copy stays in the catalogue (never-delete rule).
export const resolveLabAllFeaturesBlocker = ({ on, stage, enhance, canErase, hasErased, isBusy }: LabAllFeaturesBlockerParams): string | null => {
  if (isBusy) return 'enhance.disabled_generate'
  if (!on.stage && !on.enhance && !on.erase) return 'lab.all_features.nothing_on'
  if (on.erase && !canErase) return hasErased ? 'disabled_generate_no_new_mask' : 'disabled_generate_button'
  if (on.stage && !isLabStageSettingsValid(stage)) return 'lab.stage.invalid_keep_all'
  if (on.enhance && !isLabEnhanceSettingsValid(enhance)) return 'lab.invalid_no_feature'
  return null
}
